/*
 * FLEETA 헤더 — 모바일 드로어 커스텀 엘리먼트 <fleeta-header>
 * React Navigation.tsx의 햄버거 토글 동작 이식:
 *   - 열기/닫기 + aria-expanded 동기화
 *   - 열릴 때 첫 링크로 포커스 이동, ESC/닫기 시 토글로 복귀
 *   - 링크 클릭 시 닫기 (setIsMenuOpen(false) 대응)
 *   - 배경 스크롤 잠금(<html>.fleeta-no-scroll)
 * Dawn global.js와 태그·클래스가 분리되어 충돌 없음. define-guard로 에디터 재주입 안전.
 */
(function () {
  if (customElements.get('fleeta-header')) return;

  class FleetaHeader extends HTMLElement {
    constructor() {
      super();
      this._open = false;
      this._scrolled = false;
      this._ticking = false;
      this._onKeydown = this._onKeydown.bind(this);
      this._onViewportChange = this._onViewportChange.bind(this);
      this._onScroll = this._onScroll.bind(this);
    }

    connectedCallback() {
      // 스크롤 변형(라이브 fleeta.io): sticky일 때만 scrollY>8에서 .is-scrolled 토글 →
      // 유틸바 접힘 + 메인바가 둥근 플로팅 pill로 축소(스타일은 fleeta-header.css).
      if (this.dataset.sticky === 'true') {
        this._updateScrolled(); // 새로고침 시 이미 스크롤된 상태 반영
        window.addEventListener('scroll', this._onScroll, { passive: true });
      }

      this.toggle = this.querySelector('[data-drawer-toggle]');
      this.drawer = this.querySelector('[data-drawer]');
      if (!this.toggle || !this.drawer) return;

      this.toggle.addEventListener('click', () => {
        if (this._open) {
          this.close(true);
        } else {
          this.open();
        }
      });

      // 드로어 내 링크 클릭 → 닫기 (페이지 이동 시)
      this.drawer.addEventListener('click', (event) => {
        if (event.target.closest('a')) this.close(false);
      });

      document.addEventListener('keydown', this._onKeydown);

      // 데스크톱으로 리사이즈되면 열린 드로어 정리.
      // 브레이크포인트 단일 소스 = fleeta-header.css의 --fleeta-header-bp. 미디어쿼리는 CSS 변수를
      // 못 쓰므로 CSS엔 리터럴이 하나 남아 있지만, JS는 변수를 읽어 파생하므로 동기화 대상이 아니다.
      // 아래 폴백은 CSS 미적용 상태(에디터 부분 렌더 등) 대비 방어값.
      var bp = getComputedStyle(this).getPropertyValue('--fleeta-header-bp').trim() || '1280px';
      this._mql = window.matchMedia('(min-width: ' + bp + ')');
      if (this._mql.addEventListener) {
        this._mql.addEventListener('change', this._onViewportChange);
      } else if (this._mql.addListener) {
        this._mql.addListener(this._onViewportChange);
      }
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this._onScroll);
      document.removeEventListener('keydown', this._onKeydown);
      if (this._mql) {
        if (this._mql.removeEventListener) {
          this._mql.removeEventListener('change', this._onViewportChange);
        } else if (this._mql.removeListener) {
          this._mql.removeListener(this._onViewportChange);
        }
      }
      document.documentElement.classList.remove('fleeta-no-scroll');
    }

    _onScroll() {
      if (this._ticking) return;
      this._ticking = true;
      window.requestAnimationFrame(() => {
        this._updateScrolled();
        this._ticking = false;
      });
    }

    _updateScrolled() {
      // 라이브 실측: 5px에선 미변형, 10px에서 변형 → 임계값 8px.
      const scrolled = window.scrollY > 8;
      if (scrolled === this._scrolled) return;
      this._scrolled = scrolled;
      this.classList.toggle('is-scrolled', scrolled);
    }

    _onKeydown(event) {
      if (event.key === 'Escape' && this._open) this.close(true);
    }

    _onViewportChange() {
      if (this._mql && this._mql.matches && this._open) this.close(false);
    }

    open() {
      this.drawer.hidden = false;
      this.toggle.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('fleeta-no-scroll');
      const first = this.drawer.querySelector('a, button');
      if (first) first.focus();
      this._open = true;
    }

    close(focusToggle) {
      this.drawer.hidden = true;
      this.toggle.setAttribute('aria-expanded', 'false');
      document.documentElement.classList.remove('fleeta-no-scroll');
      this._open = false;
      if (focusToggle && this.toggle) this.toggle.focus();
    }
  }

  customElements.define('fleeta-header', FleetaHeader);
})();
